<template>
  <router-link
    v-slot="{ href, isActive, navigate }"
    :to="to"
    custom
    active-class="active"
  >
    <li :class="{ active: isActive }">
      <a
        :href="href"
        class="button clear menu-item"
        :class="{
          'is-active': isActive,
          'text-truncate': shouldTruncate,
          'is-label': labelColor,
          [`has-quality--${qualityLevel}`]: qualityLevel,
        }"
        :style="{ width: labelColor ? '' : '100%' }"
        @click="navigate"
      >
        <span v-if="icon" class="badge--icon">
          <fluent-icon class="inbox-icon" :icon="icon" size="12" />
        </span>
        <span
          :title="menuTitle"
          class="menu-label button__content"
          :class="{
            'text-truncate': shouldTruncate,
            'badge--label': labelColor,
          }"
          :style="{ backgroundColor: labelColor ? labelColor : '' }"
        >
          {{ label }}
          <span v-if="showChildCount" class="count-view">
            {{ childItemCount }}
          </span>
        </span>
        <span
          v-if="qualityLevel"
          v-tooltip.top-end="qualityTooltip"
          class="quality-dot"
          :style="{ backgroundColor: qualityDotColor }"
        />
        <span v-if="count" class="badge" :class="{ secondary: !isActive }">
          {{ count }}
        </span>
        <span v-if="warningIcon" class="badge--icon">
          <fluent-icon
            v-tooltip.top-end="$t('SIDEBAR.FACEBOOK_REAUTHORIZE')"
            class="inbox-icon"
            :icon="warningIcon"
            size="12"
          />
        </span>
      </a>
    </li>
  </router-link>
</template>
<script>
import {
  RATING_COLORS,
  normalizeRating,
} from 'dashboard/routes/dashboard/settings/inbox/helpers/qualityHistory';

export default {
  props: {
    to: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    labelColor: {
      type: String,
      default: '',
    },
    shouldTruncate: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    warningIcon: {
      type: String,
      default: '',
    },
    count: {
      type: String,
      default: '',
    },
    showChildCount: {
      type: Boolean,
      default: false,
    },
    childItemCount: {
      type: Number,
      default: 0,
    },
    qualityRating: {
      type: String,
      default: '',
    },
  },
  computed: {
    showIcon() {
      return { 'text-truncate': this.shouldTruncate };
    },
    menuTitle() {
      return this.shouldTruncate ? this.label : '';
    },
    qualityLevel() {
      return this.qualityRating ? normalizeRating(this.qualityRating) : null;
    },
    qualityDotColor() {
      return RATING_COLORS[this.qualityLevel];
    },
    qualityTooltip() {
      return this.$t(
        `SIDEBAR.INBOX_QUALITY.${this.qualityLevel.toUpperCase()}`
      );
    },
  },
};
</script>
<style lang="scss" scoped>
$badge-size: var(--space-normal);
$label-badge-size: var(--space-slab);

.button {
  margin: var(--space-small) 0;
}

.menu-item {
  display: inline-flex;
  color: var(--b-600);
  font-weight: var(--font-weight-medium);
  height: var(--space-medium);
  padding: var(--space-smaller) var(--space-smaller);
  margin: var(--space-micro) 0;
  text-align: left;
  line-height: 1.2;

  &:hover {
    background: var(--s-25);
    color: var(--s-600);
  }

  &:focus:not(.is-label) {
    border-color: var(--c-500);
  }

  &.is-active:not(.is-label) {
    background: var(--c-100);
    color: var(--c-500);
    border-color: var(--w-25);
  }
  &.is-active .count-view {
    background: var(--w-75);
    color: var(--c-500);
  }

  @each $level, $background, $text in (high, #dcfce7, #15803d),
    (medium, #fef9c3, #a16207), (low, #fee2e2, #b91c1c)
  {
    &.has-quality--#{$level}:not(.is-active) {
      background: $background;
      color: $text;

      .badge--icon {
        background: transparent;
      }
    }
  }
}

.quality-dot {
  border-radius: 50%;
  flex-shrink: 0;
  height: var(--space-small);
  margin: 0 var(--space-smaller);
  width: var(--space-small);
}

.menu-label {
  flex-grow: 1;
}

.inbox-icon {
  font-size: var(--font-size-nano);
}

.badge--label,
.badge--icon {
  display: inline-flex;
  border-radius: var(--border-radius-small);
  margin-right: var(--space-smaller);
  background: var(--s-100);
}

.badge--icon {
  align-items: center;
  height: $badge-size;
  justify-content: center;
  min-width: $badge-size;
}

.badge--label {
  padding: 3px 6px;
  border-radius: 4px;
  color: var(--white);
  font-size: var(--font-size-micro);
}

.badge.secondary {
  min-width: unset;
  background: var(--s-75);
  color: var(--s-600);
  font-weight: var(--font-weight-bold);
}

.count-view {
  background: var(--s-50);
  border-radius: var(--border-radius-normal);
  color: var(--s-600);
  font-size: var(--font-size-micro);
  font-weight: var(--font-weight-bold);
  margin-left: var(--space-smaller);
  padding: var(--space-zero) var(--space-smaller);
  line-height: var(--font-size-small);
}
</style>
